import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import logo from '../assets/logo.png';

/**
 * Navbar component renders the top navigation bar. It displays the company logo
 * and navigation links that scroll to different sections of the page. On
 * smaller screens a hamburger menu opens a sidebar overlay with glassmorphism styling.
 */
const Navbar = ({ onLinkClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
    if (onLinkClick) onLinkClick();
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Scroll detection for transparent → frosted glass transition
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header className={`navbar${isScrolled ? ' scrolled' : ''}`} id="navbar">
        <div className="logo">
          <Link to="/" onClick={handleLinkClick}>
            <img src={logo} alt="Aakeeo Technologies logo" width="150" height="50" />
          </Link>
        </div>
        <nav className="desktop-nav">
          <ul>
            <li><NavLink to="/solutions" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Solutions</NavLink></li>
            {import.meta.env.DEV && <li><NavLink to="/products" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Products</NavLink></li>}
            <li><NavLink to="/developers" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Developers</NavLink></li>
            <li><NavLink to="/resources" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Resources</NavLink></li>
            <li><NavLink to="/contact" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Contact</NavLink></li>
          </ul>
          <div className="nav-actions">
            <a href="https://calendly.com/aakeeo-technologies/crm-consultations" target="_blank" rel="noopener noreferrer" className="nav-cta">Book a Call</a>
          </div>
        </nav>

        {/* Mobile hamburger menu */}
        <button
          className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </header>

      {/* Mobile sidebar overlay */}
      <div className={`mobile-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={handleLinkClick}>
        <nav className="mobile-sidebar" onClick={(e) => e.stopPropagation()}>
          <ul>
            <li><NavLink to="/" onClick={handleLinkClick} end className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Home</NavLink></li>
            <li><NavLink to="/solutions" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Solutions</NavLink></li>
            {import.meta.env.DEV && <li><NavLink to="/products" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Products</NavLink></li>}
            <li><NavLink to="/developers" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Developers</NavLink></li>
            <li><NavLink to="/resources" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Resources</NavLink></li>
            <li><NavLink to="/contact" onClick={handleLinkClick} className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>Contact</NavLink></li>
            <li style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
              <a href="https://calendly.com/aakeeo-technologies/crm-consultations" target="_blank" rel="noopener noreferrer" className="nav-cta" onClick={handleLinkClick}>Book a Call</a>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};

export default Navbar;